// Edit layer style rule: Geometry

import { Checkbox, MenuItem, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { ILayer } from '../../../Layers/LayerInterfaces';
import { theme_errorRed, theme_limeGreen, theme_orange, theme_textColorBlended, theme_textColorMain } from '../../../Theme';
import { ILayerStyleRule_TextLabel, LAYER_STYLE_FONTS, LAYER_STYLE_TEXT_LABEL_SEPARATORS, TLayerStyleFont, TLayerStyleTextLabelSeparator } from './LayerStyleInterfaces';
import TypographyWithAutoTooltip from '../../../Components/TypograpyWithAutoTooltip';
import { CustomSelect } from '../EditLayerStyle';
import { ChangeEvent, ReactNode } from 'react';
import { MuiColorInputColors } from 'mui-color-input';
import { GetVectorLayerAttributeByID } from '../../../AttributeOps';
import { EditLayerStyleRule_Setting_Number } from './EditLayerStyleRule_Setting_Number';
import { EditLayerStyleRule_Setting_Color } from './EditLayerStyleRule_Setting_Color';
import { EditLayerStyleRule_Setting_Dropdown } from './EditLayerStyleRule_Setting_Dropdown';
import { ValidateLayerStyle_Enum, ValidateLayerStyle_Number } from './EditLayerStyleRuleGeom';


export const LAYER_STYLE_TEXT_LABEL_FONT_FAMILY_DEFAULT: TLayerStyleFont = 'Arial';
const FONT_SIZE_MIN = 6;
const FONT_SIZE_MAX = 72;
export const LAYER_STYLE_EXT_LABEL_FONT_SIZE_DEFAULT: number = 16;
export const LAYER_STYLE_TEXT_LABEL_FONT_COLOR_DEFAULT: string = '#E0B060';
export const LAYER_STYLE_TEXT_LABEL_BOLD_DEFAULT: boolean = true;
export const LAYER_STYLE_TEXT_LABEL_OPACITY_DEFAULT: number = 1.0;
const HALO_SIZE_MIN = 0;
const HALO_SIZE_MAX = 8;
export const LAYER_STYLE_TEXT_LABEL_HALO_SIZE_DEFAULT: number = 1.8;
export const LAYER_STYLE_TEXT_LABEL_HALO_COLOR_DEFAULT: string = '#000000';
export const LAYER_STYLE_TEXT_LABEL_HALO_OPACITY_DEFAULT: number = 0.75;
export const LAYER_STYLE_TEXT_LABEL_SEPARATOR_DEFAULT: TLayerStyleTextLabelSeparator = 'Round Brackets';
const WORD_WRAP_WIDTH_MIN = 0;
const WORD_WRAP_WIDTH_MAX = 500;
export const LAYER_STYLE_TEXT_LABEL_WORD_WRAP_WIDTH_DEFAULT: number = 250;
export const LAYER_STYLE_TEXT_LABEL_SINGLE_CENTER_DEFAULT: boolean = false;

export const LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_USE_DEFAULT = -1;
export const LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE = 0;

//-------------------------------------------------------------------------------
// Component props
//-------------------------------------------------------------------------------
export interface EditLayerStyleRuleTextLabelProps
{
  layer?: ILayer;
  ruleTextLabel?: ILayerStyleRule_TextLabel;   // input (if undefined, there is no geom section in the rule)
  defaultRuleTextLabel?: ILayerStyleRule_TextLabel;
  is_default_rule: boolean;
  OnRuleTextLabelUpdated: any;      // output
}

//-------------------------------------------------------------------------------
// Edit layer style rule: Text Label
//-------------------------------------------------------------------------------
export function EditLayerStyleRuleTextLabel(props: EditLayerStyleRuleTextLabelProps) 
{











  //-------------------------------------------------------------------------------
  // The primary attribute selection box was changed.
  //-------------------------------------------------------------------------------
  const OnPrimaryAttributeChanged = async (event: SelectChangeEvent<unknown>, child: ReactNode) => 
  {
    if(!props.ruleTextLabel) return;

    const newAttributeID: number = event.target.value as number;

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      primary_attribute_id: newAttributeID
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel);
  }

  //-------------------------------------------------------------------------------
  // The secondary attribute selection box was changed.
  //-------------------------------------------------------------------------------
  const OnSecondaryAttributeChanged = async (event: SelectChangeEvent<unknown>, child: ReactNode) => 
  {
    if(!props.ruleTextLabel) return;

    const newAttributeID: number = event.target.value as number;

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      secondary_attribute_id: newAttributeID
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel);
  }

  //-------------------------------------------------------------------------------
  // The font familty selection box was changed.
  //-------------------------------------------------------------------------------
  const OnFontFamilyChanged = async (event: SelectChangeEvent<unknown>, child: ReactNode) => 
  {
    if(!props.ruleTextLabel) return;

    const newFontFamily: TLayerStyleFont = event.target.value as TLayerStyleFont;

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      font_family: newFontFamily
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel);
  }

  //-------------------------------------------------------------------------------
  // The font size has changed.
  //-------------------------------------------------------------------------------
  function OnFontSizeChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.ruleTextLabel) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      size: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel);
  }

  //-------------------------------------------------------------------------------
  // The label opacity has changed.
  //-------------------------------------------------------------------------------
  function OnLabelOpacityChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.ruleTextLabel) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      opacity: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel);
  }

  //-------------------------------------------------------------------------------
  // The label color has changed.
  //-------------------------------------------------------------------------------
  function OnLabelColorChanged(value: string, colors: MuiColorInputColors)
  {
    if(!props.ruleTextLabel) return;

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      color: value
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel);    
  }

  //-------------------------------------------------------------------------------
  // The halo size has changed.
  //-------------------------------------------------------------------------------
  function OnHaloSizeChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.ruleTextLabel) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      halo_size: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel);    
  }

  //-------------------------------------------------------------------------------
  // The halo color has changed.
  //-------------------------------------------------------------------------------
  function OnHaloColorChanged(value: string, colors: MuiColorInputColors)
  {
    if(!props.ruleTextLabel) return;

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      halo_color: value
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel);    
  }

  //-------------------------------------------------------------------------------
  // The halo opacity has changed.
  //-------------------------------------------------------------------------------
  function OnHaloOpacityChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.ruleTextLabel) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      halo_opacity: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel);    
  }

  //-------------------------------------------------------------------------------
  // The BOLD checkbox was changed.
  //-------------------------------------------------------------------------------
  const OnBoldCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => 
  {
    if(!props.ruleTextLabel) return;

    // Enforce tri-state:  TRUE > FALSE > NULL
    //
    // TRUE > FALSE
    // FALSE > NULL
    // NULL > TRUE

    let tri_state_checked: boolean | undefined;
    if(props.ruleTextLabel.bold === true && checked === false)
      tri_state_checked = false;
    else if(props.ruleTextLabel.bold === false)
      tri_state_checked = undefined;
    else if(props.ruleTextLabel.bold === undefined && checked === true)
      tri_state_checked = true;

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      bold: tri_state_checked
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel); 
  }

  //-------------------------------------------------------------------------------
  // The text label separator selection box was changed.
  //-------------------------------------------------------------------------------
  const OnSeparatorChanged = async (event: SelectChangeEvent<unknown>, child: ReactNode) => 
  {
    if(!props.ruleTextLabel) return;

    const newSeparator: TLayerStyleTextLabelSeparator = event.target.value as TLayerStyleTextLabelSeparator;

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      secondary_label_separator: newSeparator
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel);
  }

  //-------------------------------------------------------------------------------
  // The word wrap width has changed.
  //-------------------------------------------------------------------------------
  function OnWordWrapWidthChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.ruleTextLabel) return;

    const newValueStr: string = event.target.value;
    const newValue: number = Number.parseFloat(newValueStr);

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      word_wrap_width: Number.isNaN(newValue) ? undefined : newValue
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel);
  }

  //-------------------------------------------------------------------------------
  // The SINGLE CENTER LABEL checkbox was changed.
  //-------------------------------------------------------------------------------
  const OnSingleCenterLabelCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => 
  {
    if(!props.ruleTextLabel) return;

    // Enforce tri-state:  TRUE > FALSE > NULL
    //
    // TRUE > FALSE
    // FALSE > NULL
    // NULL > TRUE

    let tri_state_checked: boolean | undefined;
    if(props.ruleTextLabel.single_center_label === true && checked === false)
      tri_state_checked = false;
    else if(props.ruleTextLabel.single_center_label === false)
      tri_state_checked = undefined;
    else if(props.ruleTextLabel.single_center_label === undefined && checked === true)
      tri_state_checked = true;

    let updatedRuleTextLabel: ILayerStyleRule_TextLabel =
    {
      ...props.ruleTextLabel,
      single_center_label: tri_state_checked
    }

    // Notify caller of the change
    props.OnRuleTextLabelUpdated(updatedRuleTextLabel); 
  }










  

  if(!props.layer || !props.layer.attributes || !props.ruleTextLabel || !props.defaultRuleTextLabel) return null;

  // Main render

  return (

    <Stack sx={{ mx: 0.5, my: 1 }}>

      {props.is_default_rule === false
        ?
          <Typography sx={{ mt: 0, mb: 2, color: theme_textColorMain+'70', fontSize: '0.7rem' }}>
            If a value is not set, it will be automatically inherited from the Global Defaults.  You can adjust these defaults by turning on advanced mode.
          </Typography>
        :null
      }

      {/* Attribute 1 selection */}

      <Stack direction='column' sx={{ width: '50%' }}>

        <Typography sx={{ color: theme_textColorBlended, fontSize: '0.8rem' }}>
          Attribute 1
        </Typography>

        <CustomSelect variant='standard' size='small'
                      value={props.ruleTextLabel.primary_attribute_id ?? ''}
                      onChange={(e,c)=>OnPrimaryAttributeChanged(e,c)}
                      sx={{ p: 0.5 }}>

          {props.is_default_rule === false
            ? // The "Default" special entry is not shown in the special "Defaults Rule"
              <MenuItem key={LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_USE_DEFAULT} value={LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_USE_DEFAULT}>
                <Stack>
                  <Typography sx={{ color: theme_textColorBlended+'A0', fontSize: '1.0rem' }}>
                    Default
                  </Typography>
                  <Typography sx={{ maxWidth: '300px', color: theme_textColorMain, opacity: 0.5, fontSize: '0.7rem' }}>
                    Use the default primary text label
                  </Typography>
                </Stack>
              </MenuItem>
            :null
          }

          {props.is_default_rule === true
            ? // The "None" entry is only shown in the special "Defaults Rule" - normal rules must always pick an attribute (or inherit it)
              <MenuItem key={LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE} value={LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE}>
                <Stack>
                  <Typography sx={{ color: theme_textColorBlended+'A0', fontSize: '1.0rem' }}>
                    None
                  </Typography>
                  <Typography sx={{ maxWidth: '300px', color: theme_textColorMain, opacity: 0.5, fontSize: '0.7rem' }}>
                    No primary text label
                  </Typography>
                </Stack>
              </MenuItem>
            :null
          }

          {props.layer.attributes.map(function(attribute)
          {
            // Don't show the geometry attribute
            if(attribute.attribute_name === 'geom' || attribute.attribute_name === 'geometry')
              return null;

            return (
              <MenuItem key={attribute.attribute_id} value={attribute.attribute_id}>
                <Stack>
                  <Stack direction='row' sx={{ alignItems: 'center' }}>
                    <Typography sx={{ color: theme_orange+'D0', fontSize: '1.0rem' }}>
                      {attribute.attribute_name}
                    </Typography>
                    <Typography sx={{ ml: 1, color: theme_limeGreen, opacity: 0.7, fontSize: '0.8rem' }}>
                      {attribute.unit}
                    </Typography>
                  </Stack>
                  <TypographyWithAutoTooltip sx={{ maxWidth: '300px', color: theme_textColorMain, opacity: 0.5, fontSize: '0.7rem' }}>
                    {attribute.description}
                  </TypographyWithAutoTooltip>
                </Stack>
              </MenuItem>
            )
          })}

        </CustomSelect>

        {RenderDefaultValue_Attribute(props.ruleTextLabel.primary_attribute_id, props.defaultRuleTextLabel.primary_attribute_id, props.is_default_rule, props.layer)}
        
      </Stack>

      {/* Attribute 2 (selection + separator) */}

      <Stack direction='row' sx={{ mt: 2, width: '100%' }}>

        {/* Attribute 2 selection */}

        <Stack direction='column' sx={{ width: '50%' }}>

          <Stack direction='row'>
            <Typography sx={{ color: theme_textColorBlended, fontSize: '0.8rem' }}>
              Attribute 2
            </Typography>
            <Typography sx={{ ml: 1, color: theme_textColorMain+'50', fontSize: '0.8rem' }}>
              (optional)
            </Typography>
          </Stack>

          <CustomSelect variant='standard' size='small'
                        value={props.ruleTextLabel.secondary_attribute_id ?? ''}
                        onChange={(e,c)=>OnSecondaryAttributeChanged(e,c)}
                        sx={{ p: 0.5 }}>

            {props.is_default_rule === false
              ? // The "Default" special entry is not shown in the special "Defaults Rule"
                <MenuItem key={LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_USE_DEFAULT} value={LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_USE_DEFAULT}>
                  <Stack>
                    <Typography sx={{ color: theme_textColorBlended+'A0', fontSize: '1.0rem' }}>
                      Default
                    </Typography>
                    <Typography sx={{ maxWidth: '300px', color: theme_textColorMain, opacity: 0.5, fontSize: '0.7rem' }}>
                      Use the default secondary text label
                    </Typography>
                  </Stack>
                </MenuItem>
              :null
            }

            <MenuItem key={LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE} value={LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE}>
              <Stack>
                <Typography sx={{ color: theme_textColorBlended+'A0', fontSize: '1.0rem' }}>
                  None
                </Typography>
                <Typography sx={{ maxWidth: '300px', color: theme_textColorMain, opacity: 0.5, fontSize: '0.7rem' }}>
                  No secondary text label
                </Typography>
              </Stack>
            </MenuItem>

            {props.layer.attributes.map(function(attribute)
            {
              // Don't show the geometry attribute
              if(attribute.attribute_name === 'geom' || attribute.attribute_name === 'geometry')
                return null;

              return (
                <MenuItem key={attribute.attribute_id} value={attribute.attribute_id}>
                  <Stack>
                    <Stack direction='row' sx={{ alignItems: 'center' }}>
                      <Typography sx={{ color: theme_orange+'D0', fontSize: '1.0rem' }}>
                        {attribute.attribute_name}
                      </Typography>
                      <Typography sx={{ ml: 1, color: theme_limeGreen, opacity: 0.7, fontSize: '0.8rem' }}>
                        {attribute.unit}
                      </Typography>
                    </Stack>
                    <TypographyWithAutoTooltip sx={{ maxWidth: '300px', color: theme_textColorMain, opacity: 0.5, fontSize: '0.7rem' }}>
                      {attribute.description}
                    </TypographyWithAutoTooltip>
                  </Stack>
                </MenuItem>
              )
            })}

          </CustomSelect>

          {RenderDefaultValue_Attribute(props.ruleTextLabel.secondary_attribute_id, props.defaultRuleTextLabel.secondary_attribute_id, props.is_default_rule, props.layer)}

        </Stack>

        {/* Attribute 2 separator */}

        <EditLayerStyleRule_Setting_Dropdown
              name='Attribute 2 Separator' OnChangeHandler={OnSeparatorChanged}
              value={props.ruleTextLabel.secondary_label_separator} default_value={props.defaultRuleTextLabel.secondary_label_separator} 
              is_default_rule={props.is_default_rule} margin_left={3} name_font_size='0.8rem' width='130px'
              drop_down_items={LAYER_STYLE_TEXT_LABEL_SEPARATORS}/>

      </Stack>

      {/* Font family, size, color, opacity */}

      <Stack direction='row' sx={{ width: '100%', mt: 2, justifyContent: 'space-between' }}>

        {/* Font Family selection */}

        <EditLayerStyleRule_Setting_Dropdown
              name='Font' OnChangeHandler={OnFontFamilyChanged}
              value={props.ruleTextLabel.font_family} default_value={props.defaultRuleTextLabel.font_family} 
              is_default_rule={props.is_default_rule} margin_left={0} name_font_size='0.8rem' width='100px'
              drop_down_items={LAYER_STYLE_FONTS}/>

        {/* Font Size */}

        <EditLayerStyleRule_Setting_Number 
              name='Font Size' min_value={FONT_SIZE_MIN} max_value={FONT_SIZE_MAX} OnChangeHandler={OnFontSizeChanged}
              value={props.ruleTextLabel.size} default_value={props.defaultRuleTextLabel.size} 
              is_default_rule={props.is_default_rule} margin_left={0} name_font_size='0.8rem' width='110px'/>

        {/* Label Color */}

        <EditLayerStyleRule_Setting_Color
              name='Label Color' OnChangeHandler={OnLabelColorChanged}
              value={props.ruleTextLabel.color} default_value={props.defaultRuleTextLabel.color} 
              is_default_rule={props.is_default_rule} margin_left={0} name_font_size='0.7rem' width='120px'/>        

        {/* Label Opacity */}

        <EditLayerStyleRule_Setting_Number 
              name='Label Opacity' min_value={0} max_value={1} OnChangeHandler={OnLabelOpacityChanged}
              value={props.ruleTextLabel.opacity} default_value={props.defaultRuleTextLabel.opacity} 
              is_default_rule={props.is_default_rule} margin_left={0} name_font_size='0.8rem' width='140px'/>

      </Stack>

      {/* Halo size, halo color, halo opacity */}

      <Stack direction='row' sx={{ width: '100%', mt: 2, justifyContent: 'space-between' }}>

        {/* Bold */}

        <Stack sx={{ alignItems: 'start' }}>
          <Stack direction='row' sx={{ alignItems: 'center', justifyContent: 'left', width: '90px' }}>
            <Checkbox size='small' checked={props.ruleTextLabel.bold===true} 
                      indeterminate={props.ruleTextLabel.bold===undefined}
                      onChange={(e,c)=>OnBoldCheckBoxChange(e,c)}
                      sx=
                      {{ 
                        ml: '-10px', mr: 0, color: theme_textColorBlended, width: '40px', height: '40px',
                        '&.Mui-checked': { color: theme_textColorBlended } 
                      }} />
            <Typography sx={{ color: theme_textColorMain+'A0', fontSize: '1.0rem' }}>
              Bold
            </Typography>
          </Stack>
          {RenderDefaultValue_boolean(props.ruleTextLabel.bold, props.defaultRuleTextLabel.bold, props.is_default_rule)}          
        </Stack>
        
        {/* Halo Size */}

          <EditLayerStyleRule_Setting_Number 
                name='Halo Size' min_value={HALO_SIZE_MIN} max_value={HALO_SIZE_MAX} OnChangeHandler={OnHaloSizeChanged}
                value={props.ruleTextLabel.halo_size} default_value={props.defaultRuleTextLabel.halo_size} 
                is_default_rule={props.is_default_rule} margin_left={3} name_font_size='0.8rem' width='110px'/>

        {/* Halo Color */}

        <EditLayerStyleRule_Setting_Color
              name='Halo Color' OnChangeHandler={OnHaloColorChanged}
              value={props.ruleTextLabel.halo_color} default_value={props.defaultRuleTextLabel.halo_color} 
              is_default_rule={props.is_default_rule} margin_left={2} name_font_size='0.7rem' width='120px'/>        

        {/* Halo Opacity */}

        <EditLayerStyleRule_Setting_Number 
              name='Halo Opacity' min_value={0} max_value={1} OnChangeHandler={OnHaloOpacityChanged}
              value={props.ruleTextLabel.halo_opacity} default_value={props.defaultRuleTextLabel.halo_opacity} 
              is_default_rule={props.is_default_rule} margin_left={2} name_font_size='0.8rem' width='140px'/>

      </Stack>

      {/* Text label options */}

      <Stack direction='row' sx={{ width: '100%', mt: 2, justifyContent: 'start' }}>

        {/* Word Wrap */}

        <EditLayerStyleRule_Setting_Number 
              name='Word Wrap Width' min_value={WORD_WRAP_WIDTH_MIN} max_value={WORD_WRAP_WIDTH_MAX} OnChangeHandler={OnWordWrapWidthChanged}
              value={props.ruleTextLabel.word_wrap_width} default_value={props.defaultRuleTextLabel.word_wrap_width} 
              is_default_rule={props.is_default_rule} margin_left={0} name_font_size='0.8rem' width='150px'/>

        {/* Single center label (polygon layers only) */}

        {props.layer.geoserverVectorType === 'polygon'
          ?
            <Stack sx={{ ml: 5, alignItems: 'start' }}>
              <Stack direction='row' sx={{ alignItems: 'center', justifyContent: 'left' }}>
                <Checkbox size='small' checked={props.ruleTextLabel.single_center_label===true}
                          indeterminate={props.ruleTextLabel.single_center_label===undefined}
                          onChange={(e,c)=>OnSingleCenterLabelCheckBoxChange(e,c)}
                          sx=
                          {{ 
                            ml: '-10px', mr: 0, color: theme_textColorBlended, width: '40px', height: '40px',
                            '&.Mui-checked': { color: theme_textColorBlended } 
                          }} />
                <Stack>
                  <Typography sx={{ color: theme_textColorMain+'A0', fontSize: '1.0rem' }}>
                    Single center label
                  </Typography>
                  <Typography sx={{ mt: '-4px', color: theme_textColorMain+'70', fontSize: '0.7rem' }}>
                    Ensures no duplicates
                  </Typography>
                </Stack>
              </Stack>
              {RenderDefaultValue_boolean(props.ruleTextLabel.single_center_label, props.defaultRuleTextLabel.single_center_label, props.is_default_rule)}          
            </Stack>
          :null
        }

      </Stack>

    </Stack>
  )
}



//-------------------------------------------------------------------------------
// Renders the default value/status.
// This version is specilized to handle attributes.
//-------------------------------------------------------------------------------
function RenderDefaultValue_Attribute(attrib_id: number | undefined, attrib_id_default: number | undefined, 
                                      is_default_rule: boolean, layer: ILayer)
{
  // If the value is set, or if this is happening inside the "Default Rule", we show nothing
  if((attrib_id !== undefined && attrib_id!==LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_USE_DEFAULT) || is_default_rule===true)
    return null;

  return (

    <Stack direction='row' sx={{ alignItems: 'center' }}>

      <Typography sx={{ fontSize: '0.7rem', color: theme_textColorMain+'70'}}>
        Default:
      </Typography>

      {attrib_id_default !== undefined && attrib_id_default !== LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_USE_DEFAULT && attrib_id_default !== LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE
        ?
          // We have a default value - show it
          <Typography sx={{ ml: 0.5, fontSize: '0.7rem', color: theme_orange+'A0', textTransform: 'capitalize' }}>
            {GetVectorLayerAttributeByID(layer, attrib_id_default)?.attribute_name}
          </Typography>
        : // No default value is set - show a warning
          <Typography sx={{ ml: 0.5, fontSize: '0.7rem', color: theme_errorRed}}>
            Not set
          </Typography>
      }

    </Stack>      
  )
}


//-------------------------------------------------------------------------------
// Renders the default value/status.
// This version handles BOOLEAN values.
//-------------------------------------------------------------------------------
function RenderDefaultValue_boolean(value: boolean | undefined, value_default: boolean | undefined, 
                                    is_default_rule: boolean)
{
  // If this is happening inside the "Default Rule", we show nothing
  if(is_default_rule===true)
    return null;

  // If the value is undefined, we show nothing
  if(value !== undefined)
    return null;

  return (

    <Stack direction='row' sx={{ alignItems: 'center' }}>

      <Typography sx={{ fontSize: '0.7rem', color: theme_textColorMain+'70'}}>
        Default:
      </Typography>

      {value_default !== undefined && value_default !== undefined
        ?
          // We have a default value - show it
          <Typography sx={{ ml: 0.5, fontSize: '0.7rem', color: theme_orange+'A0', textTransform: 'capitalize' }}>
            {value_default ? 'TRUE' : ' FALSE'}
          </Typography>
        : // Missing both main and default values - show a warning
          <Typography sx={{ ml: 0.5, fontSize: '0.7rem', color: theme_errorRed}}>
            Not Set
          </Typography>
      }

    </Stack>
  )
}

//-------------------------------------------------------------------------------
// Validates the specified rule text label section.
//
// Works for both regular rules and the special global defaults rule.
// 
// Returns an error message if validation FAILS, or empty string if it succeeds.
//-------------------------------------------------------------------------------
export function ValidateLayerStyleRuleTextLabel(ruleTextLabel: ILayerStyleRule_TextLabel | undefined, 
                                                default_ruleTextLabel: ILayerStyleRule_TextLabel,
                                                is_default_rule: boolean,
                                                errMsgPrefix: string): string
{
  if(!ruleTextLabel) return '';  // It's ok for a rule to not have a text label section

  // Primary attribute

  if(is_default_rule === false && 
    (ruleTextLabel.primary_attribute_id === undefined || ruleTextLabel.primary_attribute_id === LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE))
    return errMsgPrefix + 'No primary attribute selected';

  if(is_default_rule === false && 
     ruleTextLabel.primary_attribute_id === LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_USE_DEFAULT &&
     (default_ruleTextLabel.primary_attribute_id === undefined || default_ruleTextLabel.primary_attribute_id === LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE))
    return errMsgPrefix + 'No primary attribute selected';

  // Secondary attribute

  // For the special defaults rule, can't have a secondary attribute if the primary attribute is not set

  if(is_default_rule === true && 
     ruleTextLabel.primary_attribute_id === LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE &&
     ruleTextLabel.secondary_attribute_id !== LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE)
    return errMsgPrefix + 'A secondary attribute is selected, but the primary is not set';

  let errStr: string = '';

  // Secondary attribute separator
  errStr = ValidateLayerStyle_Enum('Attribute 2 Separator',ruleTextLabel.secondary_label_separator,default_ruleTextLabel.secondary_label_separator,is_default_rule,LAYER_STYLE_TEXT_LABEL_SEPARATORS,errMsgPrefix);
  if(errStr!=='') return errStr;

  // Font family
  errStr = ValidateLayerStyle_Enum('Font',ruleTextLabel.font_family,default_ruleTextLabel.font_family,is_default_rule,LAYER_STYLE_FONTS,errMsgPrefix);
  if(errStr!=='') return errStr;

  // Font size
  errStr = ValidateLayerStyle_Number('Font Size',ruleTextLabel.size,default_ruleTextLabel.size,is_default_rule,FONT_SIZE_MIN,FONT_SIZE_MAX,errMsgPrefix);
  if(errStr!=='') return errStr;

  // Label opacity
  errStr = ValidateLayerStyle_Number('Label Opacity',ruleTextLabel.opacity,default_ruleTextLabel.opacity,is_default_rule,0,1,errMsgPrefix);
  if(errStr!=='') return errStr;

  // Halo size
  errStr = ValidateLayerStyle_Number('Halo Size',ruleTextLabel.halo_size,default_ruleTextLabel.halo_size,is_default_rule,HALO_SIZE_MIN,HALO_SIZE_MAX,errMsgPrefix);
  if(errStr!=='') return errStr;

  // Halo opacity
  errStr = ValidateLayerStyle_Number('Halo Opacity',ruleTextLabel.halo_opacity,default_ruleTextLabel.halo_opacity,is_default_rule,0,1,errMsgPrefix);
  if(errStr!=='') return errStr;

  // Word wrap width
  errStr = ValidateLayerStyle_Number('Word Wrap Width',ruleTextLabel.word_wrap_width,default_ruleTextLabel.word_wrap_width,is_default_rule,WORD_WRAP_WIDTH_MIN,WORD_WRAP_WIDTH_MAX,errMsgPrefix);
  if(errStr!=='') return errStr;

  // Success
  return '';
}